/**
 * Initial-submission (NEW_SUBMISSION) form schema.
 *
 * Extracted verbatim from FormSubmissionFields.tsx as part of the
 * continuing-reviews schema-extraction refactor. The array literal is
 * unchanged; FormSubmissionFields.tsx now re-exports it as `formSections`
 * so every existing import keeps working byte-for-byte.
 *
 * The FormField/FormSection interfaces remain defined in FormSubmissionFields.tsx
 * (continuingReviewForm.ts imports them from there); we re-export them here for
 * convenience.
 */

import { FormSection, validateDocument } from '../components/FormSubmissionFields';

export type { FormField, FormSection } from '../components/FormSubmissionFields';

/** Full initial-submission formSections with all the sections included. */
export const initialSubmissionFormSections: FormSection[] = [
  {
    heading: 'Basic Study Protocol Information',
    items: [
      {
        itemType: 'question',
        name: 'ref_name',
        label: 'Enter a <b>reference name</b> for this submission:',
        fieldType: 'text',
        placeholder: 'short version of protocol name for easy reference, e.g., ABC study',
        validation: {
          required: true,
          maxLength: 30,
          pattern: /^.*$/,
        },
      },
      {
        itemType: 'question',
        name: 'protocol_title',
        label: 'Enter the <b>full title</b> of your research protocol:',
        fieldType: 'text',
        placeholder: 'e.g., Assessment of [Variable/Condition] in [Population/Context]',
        validation: {
          required: true,
          maxLength: 300,
          pattern: /^.*$/,
        },
      },
      {
        itemType: 'question',
        name: 'sponsor_name',
        label: 'What institution is sponsoring this study?',
        fieldType: 'text',
        placeholder: 'e.g., ACME Inc.',
        validation: {
          required: true,
          maxLength: 100,
          pattern: /^.*$/,
        },
      },
      {
        itemType: 'question',
        name: 'is_exempt_determination',
        label:
          'Are you applying for Exempt Determination Review or a Not Human Subjects Research (NHSR) determination, i.e., do you believe your study qualifies for an exempt category or is not human subjects research, and you would like Timely IRB to assess your study for exempt or NHSR status?<br><small>There are 8 categories of exempt research; review <a href="https://www.ecfr.gov/current/title-45/subtitle-A/subchapter-A/part-46/subpart-A/section-46.104" target="_blank" rel="noopener noreferrer" style="color: blue;">45 CFR 46.104(d)</a> if you are unsure.</small>',
        fieldType: 'radio',
        options: ['Yes', 'No'],
        validation: { required: true },
      },
      {
        itemType: 'question',
        name: 'is_multi_pi',
        label:
          'Is there more than one Principal Investigator (PI) for this research?<br><small>e.g., a lead PI and a site PI for each geographically distinct site</small>',
        fieldType: 'radio',
        options: ['Yes', 'No'],
        validation: { required: true },
        dependency: { field: 'is_exempt_determination', value: 'No' },
      },
      {
        itemType: 'instruction',
        label:
          'You will need to upload resumes or CVs for all PIs in the document upload section of this form, and provide the lead PI\'s information in the form below.',
        dependency: { field: 'is_multi_pi', value: 'Yes' },
      },
      {
        itemType: 'question',
        name: 'is_central_irb',
        label: 'Has the Sponsor or CRO designated this IRB as the central IRB for this study?',
        fieldType: 'radio',
        options: ['Yes', 'No'],
        validation: { required: true },
        dependency: { all: [{ field: 'is_multi_pi', value: 'Yes' }, { field: 'is_exempt_determination', value: 'No' }] },
      },
      {
        itemType: 'question',
        name: 'cro_smo_involvement',
        label: 'Is a Contract Research Organization (CRO) and/or Site Management Organization (SMO) involved in the research?',
        fieldType: 'radio',
        options: ['Yes', 'No'],
        validation: { required: true },
        dependency: { field: 'is_exempt_determination', value: 'No' },
      },
      {
        itemType: 'question',
        name: 'cro_smo_name',
        label: 'Enter the name of the CRO and/or SMO:',
        fieldType: 'text',
        validation: {
          required: true,
          maxLength: 200,
          pattern: /^.*$/,
        },
        dependency: { field: 'cro_smo_involvement', value: 'Yes' },
      },
      {
        itemType: 'question',
        name: 'is_non-us',
        label: 'Is any of the proposed research going to be conducted outside the United States?',
        fieldType: 'radio',
        options: ['Yes', 'No'],
        validation: { required: true },
        dependency: { field: 'is_exempt_determination', value: 'No' },
      },
      {
        itemType: 'question',
        name: 'non_us_location',
        label: 'Enter the name of the non-US location(s):',
        fieldType: 'text',
        validation: {
          required: true,
          maxLength: 300,
          pattern: /^.*$/,
        },
        dependency: { field: 'is_non-us', value: 'Yes' },
      },
      {
        itemType: 'question',
        name: 'is_fed_funded',
        label: 'Is this research funded, supported, or conducted by a United States federal department or agency?',
        fieldType: 'radio',
        options: ['Yes', 'No'],
        validation: { required: true },
      },
      {
        itemType: 'instruction',
        label:
          'You will need to upload written documentation of the Federal Wide Assurance (FWA) for each organization involved with the research in the document upload section of this form.',
        dependency: { field: 'is_fed_funded', value: 'Yes' },
      },
      {
        itemType: 'question',
        name: 'fed_funding_agency',
        label:
          'Select the federal department or agency funding the research (you may select more than one, if applicable):',
        fieldType: 'checkboxes',
        options: [
          'Agency for International Development',
          'Department of Agriculture',
          'Bureau of Prisons, Department of Justice',
          'Central Intelligence Agency',
          'Consumer Product Safety Commission',
          'Corporation for National and Community Service',
          'Department of Commerce',
          'Department of Defense',
          'Department of Education',
          'Department of Energy',
          'Department of Health and Human Services',
          'Centers for Disease Control',
          'Environmental Protection Agency',
          'Federal Bureau of Investigation, Department of Justice',
          'National Aeronautics and Space Administration',
          'National Institutes of Health',
          'Department of Homeland Security',
          'Department of Housing and Urban Development',
          'Office of Justice Program, Department of Justice',
          'Office of the Director of National Intelligence',
          'Social Security Administration',
          'Department of Transportation',
          'Department of Veterans Affairs',
        ],
        validation: { required: true },
        dependency: { field: 'is_fed_funded', value: 'Yes' },
      },
      {
        itemType: 'question',
        name: 'fda_submit',
        label:
          'Will you or others be submitting data from this research to the FDA (e.g., in a 510(k) or NDA or PMA submission) or hold data for inspection by the FDA (e.g., as part of the Design History File)?',
        fieldType: 'radio',
        options: ['Yes', 'No'],
        validation: { required: true },
      },
      {
        itemType: 'question',
        name: 'fda_research_type',
        label:
          'The category of FDA-regulated product being investigated or otherwise evaluated through this research is (select all that apply):',
        fieldType: 'checkboxes',
        options: ['Medical device', 'Drug/Biologic', 'Food', 'Radioactive material', 'Other'],
        validation: { required: true },
        dependency: { field: 'fda_submit', value: 'Yes' },
      },
      {
        itemType: 'instruction',
        label: 'Select "medical device" if your investigational product is an In Vitro Diagnostic or Software as a Medical Device.',
        dependency: { field: 'fda_submit', value: 'Yes' }
      },
      {
        itemType: 'question',
        name: 'fda_research_type_other',
        label: 'If Other, describe the type of FDA-regulated research:',
        fieldType: 'text',
        validation: {
          required: true,
          maxLength: 500,
          pattern: /^.*$/,
        },
        dependency: { all: [{ field: 'fda_research_type', value: 'Other' }, { field: 'fda_submit', value: 'Yes' }] },
      },
      {
        itemType: 'question',
        name: 'ind_conduct',
        label: 'Are you conducting the research under an Investigational New Drug Application (IND)?',
        fieldType: 'radio',
        options: ['Yes', 'No'],
        validation: { required: true },
      },
      {
        itemType: 'question',
        name: 'is_single_patient_ind',
        label:
          'Is this a <b>single-patient IND</b> (spIND) / expanded-access use of the investigational drug for one patient?',
        fieldType: 'radio',
        options: ['Yes', 'No'],
        validation: { required: true },
        dependency: {
          field: 'ind_conduct',
          value: 'Yes',
        },
      },
      {
        itemType: 'question',
        name: 'spind_expanded_access_category',
        label:
          'If Yes, select the type of single-patient IND / expanded-access use:',
        fieldType: 'dropdown-single',
        options: [
          'Non-emergency single-patient expanded access',
          'Emergency single-patient use',
        ],
        validation: { required: true },
        dependency: {
          field: 'is_single_patient_ind',
          value: 'Yes',
        },
      },
      {
        itemType: 'question',
        name: 'spind_emergency_already_administered',
        label:
          'For an <b>emergency single-patient use</b>, has the investigational drug already been administered before IRB review?',
        fieldType: 'radio',
        options: ['Yes', 'No'],
        validation: { required: true },
        dependency: {
          all: [
            { field: 'is_single_patient_ind', value: 'Yes' },
            { field: 'spind_expanded_access_category', value: 'Emergency single-patient use' },
          ],
        },
      },
      {
        itemType: 'question',
        name: 'spind_emergency_use_datetime',
        label:
          'If Yes, enter the date and approximate time the emergency use occurred:',
        fieldType: 'text',
        placeholder: 'e.g., 01/15/2025 14:30',
        validation: {
          required: true,
          maxLength: 20,
          pattern: /^[0-9/:\sAPMapm-]+$/,
        },
        dependency: {
          all: [
            { field: 'is_single_patient_ind', value: 'Yes' },
            { field: 'spind_expanded_access_category', value: 'Emergency single-patient use' },
            { field: 'spind_emergency_already_administered', value: 'Yes' },
          ],
        },
      },
      {
        itemType: 'question',
        name: 'ind_number',
        label: 'Enter the <b>IND number</b> assigned by the FDA:',
        fieldType: 'text',
        placeholder: 'e.g., 123456',
        validation: {
          required: true,
          maxLength: 20,
          pattern: /^[0-9]+$/,
        },
        dependency: {
          field: 'ind_conduct',
          value: 'Yes',
        },
      },
      {
        itemType: 'question',
        name: 'ind_sponsor_name',
        label:
          'Who is the <b>IND sponsor</b> (the person or entity that submitted the IND to FDA)?',
        fieldType: 'text',
        placeholder: 'e.g., Dr. Jane Smith; ACME Biotech, Inc.',
        validation: {
          required: true,
          maxLength: 200,
          pattern: /^.*$/,
        },
        dependency: {
          field: 'ind_conduct',
          value: 'Yes',
        },
      },
      {
        itemType: 'question',
        name: 'ind_sponsor_is_pi',
        label: 'Is the PI also the IND sponsor (i.e., a sponsor-investigator)?',
        fieldType: 'radio',
        options: ['Yes', 'No'],
        validation: { required: true },
        dependency: {
          field: 'ind_conduct',
          value: 'Yes',
        },
      },
      {
        itemType: 'question',
        name: 'ind_submission_date',
        label: 'Date the IND was submitted (or is planned to be submitted) to the FDA:',
        fieldType: 'text',
        placeholder: 'use MM/DD/YYYY format',
        validation: {
          required: true,
          maxLength: 10,
          pattern: /^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])\/[0-9]{4}$/,
        },
        dependency: {
          field: 'ind_conduct',
          value: 'Yes',
        },
      },
      {
        itemType: 'question',
        name: 'ind_safe_to_proceed',
        label:
          'Has the FDA authorized use under this IND (no clinical hold / “safe to proceed”)?',
        fieldType: 'radio',
        options: ['Yes', 'No', 'Pending'],
        validation: { required: true },
        dependency: {
          field: 'ind_conduct',
          value: 'Yes',
        },
      },
      {
        itemType: 'question',
        name: 'ind_authorization_date',
        label: 'If Yes, enter the date of the FDA “safe to proceed” letter or end of 30-day review period:',
        fieldType: 'text',
        placeholder: 'use MM/DD/YYYY format',
        validation: {
          required: true,
          maxLength: 10,
          pattern: /^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])\/[0-9]{4}$/,
        },
        dependency: {
          all: [
            { field: 'ind_conduct', value: 'Yes' },
            { field: 'ind_safe_to_proceed', value: 'Yes' },
          ],
        },
      },
      {
        itemType: 'question',
        name: 'ide_conduct',
        label: 'Are you conducting the research under an Investigation Device Exemption (IDE)?',
        fieldType: 'radio',
        options: ['Yes', 'No'],
        validation: { required: true },
        dependency: { all: [{ field: 'fda_research_type', value: 'Medical device' }, { field: 'fda_submit', value: 'Yes' }] },
      },
      {
        itemType: 'question',
        name: 'nsr_conduct',
        label: 'Are you conducting the research under the Abbreviated IDE (NSR) requirements?',
        fieldType: 'radio',
        options: ['Yes', 'No'],
        validation: { required: true },
        dependency: { all: [{ field: 'fda_research_type', value: 'Medical device' }, { field: 'fda_submit', value: 'Yes' }] },
      },
      {
        itemType: 'question',
        name: 'evaluate_fda_device',
        label: 'Are you conducting the research to evaluate a previously FDA-cleared or FDA-approved device or drug or biologic?',
        fieldType: 'radio',
        options: ['Yes', 'No'],
        validation: { required: true },
      },
      {
        itemType: 'question',
        name: 'vulnerable_populations',
        label: 'Are any of the populations below involved in your research? Select all that apply:',
        fieldType: 'dropdown-multiple',
        options: [
          'Subjects who are adults unable to consent',
          'Subjects who are children',
          'Children who are wards of the state',
          'Non-viable neonates',
          'Neonates of uncertain viability',
          'Subjects who are prisoners',
          'Subjects who are pregnant at enrollment',
          'Subjects who become pregnant while on study',
          'None of the above',
        ],
        validation: { required: true },
      },
      {
        itemType: 'question',
        name: 'studentsoremployees_epa_genetransfer',
        label:
          'Does the research involve ANY of the following: (1) research participants who are students or employees of the investigators or sponsor; (2) collection of data to be submitted to the Environmental Protection Agency; (3) any form of human gene transfer as described in Section III-C of the NIH Guidelines?',
        fieldType: 'radio',
        options: ['Yes', 'No'],
        validation: { required: true },
      },
      {
        itemType: 'question',
        name: 'subjects_students_employees',
        label:
          'Will the research involve subjects who are students or employees of the investigators?',
        fieldType: 'radio',
        options: ['Yes', 'No'],
        validation: { required: true },
        dependency: { field: 'studentsoremployees_epa_genetransfer', value: 'Yes' },
      },
      {
        itemType: 'question',
        name: 'epa_submit',
        label: 'Will you or others submit data from this research to the US Environmental Protection Agency (EPA)?',
        fieldType: 'radio',
        options: ['Yes', 'No'],
        validation: { required: true },
        dependency: { field: 'studentsoremployees_epa_genetransfer', value: 'Yes' },
      },
      {
        itemType: 'question',
        name: 'human_gene_transfer',
        label:
          'Does this research involve any form of human gene transfer as described in Section III-C of the NIH Guidelines?',
        fieldType: 'radio',
        options: ['Yes', 'No'],
        validation: { required: true },
        dependency: { field: 'studentsoremployees_epa_genetransfer', value: 'Yes' },
      },
      {
        itemType: 'question',
        name: 'special_committees',
        label:
          'To the best of your knowledge, does this study require an independent Data Monitoring, Data Safety Monitoring or Endpoint Adjudication Committee per federal regulations or sponsor policy?',
        fieldType: 'radio',
        options: ['Yes', 'No'],
        validation: { required: true },
        dependency: { field: 'is_exempt_determination', value: 'No' },
      },
      {
        itemType: 'question',
        name: 'dm_committee',
        label:
          'Does this study require an independent Data Monitoring Committee / Data Safety Monitoring Board?',
        fieldType: 'radio',
        options: ['Yes', 'No'],
        validation: { required: true },
        dependency: { field: 'special_committees', value: 'Yes' },
      },
      {
        itemType: 'question',
        name: 'dm_service_provider',
        label: 'Has a service provider already been selected for the Data Monitoring Committee?',
        fieldType: 'radio',
        options: ['Yes', 'No'],
        validation: { required: true },
        dependency: { field: 'special_committees', value: 'Yes' },
      },
      {
        itemType: 'question',
        name: 'dm_service_provider_name',
        label: 'If Yes, provide the name of the service provider for the Data Monitoring Committee:',
        fieldType: 'text',
        placeholder: 'DMC service provider name',
        validation: {
          required: true,
          maxLength: 200,
          pattern: /^.*$/,
        },
        dependency: { field: 'special_committees', value: 'Yes' },
      },
      {
        itemType: 'question',
        name: 'endpoint_committee',
        label: 'Does this study require an independent Endpoint Adjudication Committee?',
        fieldType: 'radio',
        options: ['Yes', 'No'],
        validation: { required: true },
        dependency: { field: 'special_committees', value: 'Yes' },
      },
      {
        itemType: 'question',
        name: 'endpoint_service_provider',
        label: 'Has a service provider already been selected for the Endpoint Adjudication Committee?',
        fieldType: 'radio',
        options: ['Yes', 'No'],
        validation: { required: true },
        dependency: { field: 'special_committees', value: 'Yes' },
      },
      {
        itemType: 'question',
        name: 'endpoint_service_provider_name',
        label: 'If Yes, provide the name of the service provider for the Endpoint Adjudication Committee:',
        fieldType: 'text',
        placeholder: 'EAC service provider name',
        validation: {
          required: true,
          maxLength: 200,
          pattern: /^.*$/,
        },
        dependency: { field: 'special_committees', value: 'Yes' },
      },
      {
        itemType: 'question',
        name: 'pii_or_biospecimen',
        label: 'Does this research involve the collection of identifiable private information or identifiable biospecimens during study procedures?',
        fieldType: 'radio',
        options: ['Yes', 'No'],
        validation: { required: true },
        dependency: { field: 'is_exempt_determination', value: 'No' },
      },
      {
        itemType: 'question',
        name: 'secondary_research',
        label: 'Select one option regarding secondary research:',
        fieldType: 'radio',
        options: [
          'There is a possibility that identifiers might be removed from the identifiable private information or biospecimens, and after such removal, the information/biospecimens may be used for future research or distributed to another investigator without additional informed consent.',
          'Subject information or identifiable biospecimens collected as part of the research, even if identifiers are removed, will not be used or distributed for future research.',
        ],
        validation: { required: true },
        dependency: { all: [{ field: 'pii_or_biospecimen', value: 'Yes' }, { field: 'is_exempt_determination', value: 'No' }] },
      },
    ],
  },
  {
    heading: 'Informed Consent, Participant Compensation, Privacy, and Confidentiality',
    items: [
      {
        itemType: 'question',
        name: 'informed_consent',
        label: 'Will study participants or their representatives provide informed consent to take part in this research?',
        fieldType: 'radio',
        options: ['Yes', 'No'],
        validation: { required: true },
      },
      {
        itemType: 'question',
        name: 'waiver_request_reason',
        label: 'It appears you are requesting a waiver of informed consent. Describe the reason for the waiver request<br><small>If submitting study for Exempt Determination Review, enter "exempt research - informed consent not required".</small>:',
        fieldType: 'text',
        rows: 3,
        validation: {
          required: true,
          maxLength: 1000,
          pattern: /^[\s\S]*$/,
        },
        dependency: { field: 'informed_consent', value: 'No' },
      },
      {
        itemType: 'question',
        name: 'consent_setting',
        label: 'Select the setting of the consent process:',
        fieldType: 'dropdown-multiple',
        options: [
          'Private room',
          'Waiting room',
          'Over the phone',
          'Online',
          'Open Ward',
          'Emergency setting',
          'Group setting',
          'In public',
          'Other',
        ],
        validation: { required: true },
        dependency: { field: 'informed_consent', value: 'Yes' },
      },
      {
        itemType: 'instruction',
        label:
          'The research team must:' +
          '<ul><li> - Give the person providing informed consent as much time as needed to decide.</li>' +
          '<li>- If more time is needed than allowed by the research design, do not enroll the participant.</li>' +
          '<li>- Evaluate whether the person is experiencing time pressure and, if so, do not enroll. </li>' +
          '<li>- Evaluate for coercion or undue influence, and do not enroll if detected.</li>' +
          '<li>- Invite and answer questions about the research.</li></ul>',
        dependency: { field: 'informed_consent', value: 'Yes' },
      },
      {
        itemType: 'question',
        name: 'consent_process_complete',
        label: 'Will the research team perform all the above?',
        fieldType: 'radio',
        options: ['Yes', 'No'],
        validation: { required: true },
        dependency: { field: 'informed_consent', value: 'Yes' },
      },
      {
        itemType: 'question',
        name: 'written_consent',
        label: 'Will subjects or their representatives sign a consent form physically or electronically?<br><small>Answer "yes" even if consent will be provided verbally (e.g., over the phone), but a study operator records the consent physically or electronically for each participant.</small>',
        fieldType: 'radio',
        options: ['Yes', 'No'],
        validation: { required: true },
        dependency: { field: 'informed_consent', value: 'Yes' },
      },
      {
        itemType: 'question',
        name: 'signature_method',
        label: 'How will signatures be obtained?',
        fieldType: 'dropdown-single',
        options: ['Wet Ink', 'Electronic', 'Both'],
        validation: { required: true },
        dependency: { all: [{ field: 'written_consent', value: 'Yes' }, { field: 'informed_consent', value: 'Yes' }] },
      },
      {
        itemType: 'question',
        name: 'fda_part11_compliance',
        label:
          'Is the signature documentation on the online consent form FDA Part 11 compliant as a legal signature?',
        fieldType: 'radio',
        options: ['Yes', 'No'],
        validation: { required: true },
        dependency: { all: [{ field: 'signature_method', value: 'Electronic' }, { field: 'informed_consent', value: 'Yes' }, { field: 'fda_submit', value: 'Yes' }] },
      },
      {
        itemType: 'instruction',
        label:
          'Consent Documentation Process: The research team must:' +
          '<ul><li>- Give adequate opportunity to read, or listen to, the consent document before signing.</li>' +
          '<li>- Ensure the consent document is signed and dated by the person providing consent or their representative, or that the verbal consent is recorded electronically.</li>' +
          '<li>- Provide a signed and dated copy of the consent document to the person.</li>' +
          '<li>- If the person cannot read, have an impartial witness present who signs and dates the document.</li></ul>',
        dependency: { field: 'informed_consent', value: 'Yes' },
      },
      {
        itemType: 'question',
        name: 'consent_form_processing_complete',
        label: 'Will the research team perform all the above steps?',
        fieldType: 'radio',
        options: ['Yes', 'No'],
        validation: { required: true },
        dependency: { field: 'informed_consent', value: 'Yes' },
      },
      {
        itemType: 'question',
        name: 'subject_payment',
        label:
          'Will you compensate subjects for participation (including reimbursements such as travel, parking, meals, etc.)?',
        fieldType: 'radio',
        options: ['Yes', 'No'],
        validation: { required: true },
        dependency: { field: 'is_exempt_determination', value: 'No' },
      },
      {
        itemType: 'warning',
        label:
          'Please ensure that you have incorporated details about participant compensation into the study protocol & payment language in the consent form uploaded with this application!',
        dependency: { field: 'subject_payment', value: 'Yes' },
      },
      {
        itemType: 'question',
        name: 'privacy_private_setting',
        label:
          'Will you or others perform all study procedures involving participants in a private setting?',
        fieldType: 'radio',
        options: ['Yes', 'No'],
        validation: { required: true },
        dependency: { field: 'is_exempt_determination', value: 'No' },
      },
      {
        itemType: 'question',
        name: 'privacy_procedures',
        label: 'Describe what procedures will be followed to protect the privacy of participants:',
        fieldType: 'text',
        rows: 3,
        placeholder: 'Describe procedures to protect participant privacy',
        validation: {
          required: true,
          maxLength: 5000,
          pattern: /^[\s\S]*$/,
        },
        dependency: { field: 'privacy_private_setting', value: 'No' },
      },
      {
        itemType: 'question',
        name: 'confidentiality_hipaa_compliance',
        label: 'Will your organization be subject to and in compliance with HIPAA for the duration of the study?<br><small>(if yes, you can skip the questions that will otherwise pop up below about confidentiality procedures)</small>',
        fieldType: 'radio',
        options: ['Yes', 'No'],
        validation: { required: true },
        dependency: { field: 'is_exempt_determination', value: 'No' },
      },
      {
        itemType: 'question',
        name: 'confidentiality_secure_storage',
        label:
          'Will you maintain paper records and electronic equipment containing confidential information in a physically secure location?',
        fieldType: 'radio',
        options: ['Yes', 'No'],
        validation: { required: true },
        dependency: { field: 'confidentiality_hipaa_compliance', value: 'No' },
      },
      {
        itemType: 'question',
        name: 'confidentiality_staff_training',
        label: 'Will staff be trained on confidentiality procedures?',
        fieldType: 'radio',
        options: ['Yes', 'No'],
        validation: { required: true },
        dependency: { field: 'confidentiality_hipaa_compliance', value: 'No' },
      },
      {
        itemType: 'question',
        name: 'confidentiality_access_limit',
        label: 'Will you limit access to confidentiality data on a need-to-know basis?',
        fieldType: 'radio',
        options: ['Yes', 'No'],
        validation: { required: true },
        dependency: { field: 'confidentiality_hipaa_compliance', value: 'No' },
      },
      {
        itemType: 'question',
        name: 'confidentiality_remove_identifiers',
        label: 'Will you remove identifiers as soon as feasible?',
        fieldType: 'radio',
        options: ['Yes', 'No'],
        validation: { required: true },
        dependency: { field: 'confidentiality_hipaa_compliance', value: 'No' },
      },
      {
        itemType: 'question',
        name: 'confidentiality_encrypt_storage',
        label:
          'Will you encrypt confidential data stored electronically? <br><small>(desktop, servers, mobile devices, removable media, etc.)</small>',
        fieldType: 'radio',
        options: ['Yes', 'No'],
        validation: { required: true },
        dependency: { field: 'confidentiality_hipaa_compliance', value: 'No' },
      },
      {
        itemType: 'question',
        name: 'confidentiality_encrypt_transmission',
        label: 'Will you encrypt confidential data transmitted over the Internet (including email)?',
        fieldType: 'radio',
        options: ['Yes', 'No'],
        validation: { required: true },
        dependency: { field: 'confidentiality_hipaa_compliance', value: 'No' },
      },
      {
        itemType: 'question',
        name: 'confidentiality_additional_methods',
        label: 'Describe any additional procedures to protect confidentiality (optional):',
        fieldType: 'text',
        rows: 3,
        placeholder: '(optional) Describe additional confidentiality procedures',
        validation: {
          required: false,
          maxLength: 5000,
          pattern: /^[\s\S]*$/,
        },
        dependency: { field: 'confidentiality_hipaa_compliance', value: 'No' },
      },
      {
        itemType: 'question',
        name: 'hipaa_waiver',
        label: 'Are you requesting the IRB to consider a full or partial waiver of HIPAA authorization as part of this study?<br><small>answer "yes"if your study requires you to access Protected Health Information (PHI) without explicit and full participant authorization to access/use PHI</small>',
        fieldType: 'radio',
        options: ['Yes', 'No'],
        validation: { required: true },
        dependency: { field: 'confidentiality_hipaa_compliance', value: 'No' },
      },
      {
        itemType: 'question',
        name: 'hipaa_waiver_option',
        label:
          'Which of the following HIPAA waiver options are you requesting the IRB to consider as part of this study?',
        fieldType: 'radio',
        options: [
          'Partial waiver of authorization for access to records for subject recruitment or screening<br><small>(i.e., access PHI from patient records before consent to identify, screen, or recruit potential research participants)</small>',
          'Partial waiver of authorization for waiver of signing an authorization form<br><small>(i.e., use or disclose PHI with verbal consent,without obtaining the participant’s signature on a formal HIPAA authorization form)</small>',
          'Full waiver of authorization<br><small>(i.e., access, use, or disclose participant PHI without verbal or written consent)</small>',
        ],
        validation: { required: true },
        dependency: { field: 'hipaa_waiver', value: 'Yes' },
      },
      {
        itemType: 'warning',
        label:
          'By submitting this form, I confirm that:' +
          '<ul><li>- The research team will comply with HIPAA to secure protected health information.</li>' +
          '<li>- The research team will use/disclose information only as allowed by HIPAA.</li></ul>',
        dependency: { field: 'hipaa_waiver', value: 'Yes' },
      },
      {
        itemType: 'question',
        name: 'hipaa_identifiers',
        label: 'Describe or list the identifiers planned to be used or disclosed:',
        fieldType: 'text',
        rows: 3,
        validation: {
          required: true,
          maxLength: 1000,
          pattern: /^[\s\S]*$/,
        },
        dependency: { field: 'hipaa_waiver', value: 'Yes' },
      },
      {
        itemType: 'question',
        name: 'hipaa_necessity',
        label: 'Explain why access to the protected health information is necessary:',
        fieldType: 'text',
        rows: 3,
        validation: {
          required: true,
          maxLength: 1000,
          pattern: /^[\s\S]*$/,
        },
        dependency: { field: 'hipaa_waiver', value: 'Yes' },
      },
      {
        itemType: 'question',
        name: 'hipaa_destroy_plan',
        label: 'Describe the plan to destroy the identifiers (or justify their retention):',
        fieldType: 'text',
        rows: 3,
        validation: {
          required: true,
          maxLength: 1000,
          pattern: /^[\s\S]*$/,
        },
        dependency: { field: 'hipaa_waiver', value: 'Yes' },
      },
      {
        itemType: 'question',
        name: 'hipaa_waiver_justification',
        label: 'Explain why the research could not practicably be conducted without the waiver:',
        fieldType: 'text',
        rows: 3,
        validation: {
          required: true,
          maxLength: 1000,
          pattern: /^[\s\S]*$/,
        },
        dependency: { field: 'hipaa_waiver', value: 'Yes' },
      },
      {
        itemType: 'question',
        name: 'hipaa_without_access',
        label:
          'Explain why the research could not practicably be conducted without access to and use of the protected health information:',
        fieldType: 'text',
        rows: 3,
        validation: {
          required: true,
          maxLength: 1000,
          pattern: /^[\s\S]*$/,
        },
        dependency: { field: 'hipaa_waiver', value: 'Yes' },
      },
    ],
  },
  {
    heading: 'Principal Investigator & Research Team Information',
    description:
      'Add information about the lead principal investigator for the study below.',
    items: [
      {
        itemType: 'question',
        name: 'pi_first_name',
        label: 'PI First Name:',
        fieldType: 'text',
        validation: {
          required: true,
          maxLength: 100,
          pattern: /^.*$/,
        },
      },
      {
        itemType: 'question',
        name: 'pi_last_name',
        label: 'PI Last Name:',
        fieldType: 'text',
        validation: {
          required: true,
          maxLength: 100,
          pattern: /^.*$/,
        },
      },
      {
        itemType: 'question',
        name: 'pi_degree',
        label: 'PI\'s highest degree:',
        fieldType: 'dropdown-single',
        options: ['MD/PhD', 'MD', 'PhD', 'Bachelor\'s', 'Master\'s', 'Some College', 'High School'],
        validation: {
          required: true,
        },
      },
      {
        itemType: 'question',
        name: 'pi_organization',
        label: 'PI\'s primary affiliation - company/institution:',
        fieldType: 'text',
        validation: {
          required: true,
          maxLength: 200,
          pattern: /^.*$/,
        },
      },
      {
        itemType: 'question',
        name: 'pi_email',
        label: 'PI\'s work email:',
        fieldType: 'text',
        validation: {
          required: true,
          maxLength: 254,
          pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        },
      },
      {
        itemType: 'question',
        name: 'pi_phone',
        label: 'PI US phone (10 digits, no country code):',
        fieldType: 'text',
        validation: {
          required: true,
          minLength: 10,
          maxLength: 10,
          pattern: /^[0-9]+$/,
        },
      },
      {
        itemType: 'question',
        name: 'pi_address_line_1',
        label: 'PI Address Line 1:',
        fieldType: 'text',
        validation: {
          required: true,
          maxLength: 200,
          pattern: /^.*$/,
        },
      },
      {
        itemType: 'question',
        name: 'pi_address_line_2',
        label: 'PI Address Line 2:',
        fieldType: 'text',
        validation: {
          required: false,
          maxLength: 200,
          pattern: /^.*$/,
        },
      },
      {
        itemType: 'question',
        name: 'pi_city',
        label: 'PI\'s city:',
        fieldType: 'text',
        validation: {
          required: true,
          maxLength: 100,
          pattern: /^.*$/,
        },
      },
      {
        itemType: 'question',
        name: 'pi_state',
        label: 'PI\'s state:',
        fieldType: 'text',
        validation: {
          required: true,
          maxLength: 100,
          pattern: /^.*$/,
        },
      },
      {
        itemType: 'question',
        name: 'pi_zip_code',
        label: 'PI\'s zip code:',
        fieldType: 'text',
        validation: {
          required: true,
          maxLength: 6,
          pattern: /^.*$/,
        },
      },
      {
        itemType: 'question',
        name: 'pi_country',
        label: 'Enter the PI\'s country:',
        fieldType: 'text',
        validation: {
          required: true,
          maxLength: 100,
          pattern: /^.*$/,
        },
      },
      {
        itemType: 'question',
        name: 'pi_profile_links',
        label: 'PI\'s profile/bio links:',
        fieldType: 'text',
        placeholder: 'input at least one, and as many, links as you\'d like - CV, department bio, LinkedIn, Google Scholar, personal website, lab website, etc.',
        rows: 3,
        validation: {
          required: true,
          maxLength: 1000,
          pattern: /^[\s\S]*$/,
        },
      },
      {
        itemType: 'question',
        name: 'is_pi_physician',
        label: 'Is the PI a practicing physician?',
        fieldType: 'radio',
        options: ['Yes', 'No'],
        validation: { required: true },
      },
      {
        itemType: 'question',
        name: 'pi_npi_number',
        label: 'Enter the PI\'s NPI number:<br><small>You can look it up <b><span style="color: blue;"><a href="https://npiregistry.cms.hhs.gov/search" target="_blank" rel="noopener noreferrer">HERE</a></span></b></small>',
        fieldType: 'text',
        validation: {
          required: true,
          minLength: 10,
          maxLength: 10,
          pattern: /^[0-9]+$/,
        },
        dependency: { field: 'is_pi_physician', value: 'Yes' },
      },
      {
        itemType: 'question',
        name: "pi_specialty",
        label: 'Select the PI\'s specialty (you may select multiple options):',
        fieldType: 'dropdown-multiple',
        options: ['Allergy and Immunology', 'Anesthesiology', 'Cardiology', 'Dermatology', 'Emergency Medicine', 'Endocrinology', 'Family Medicine', 'Gastroenterology', 'General Surgery', 'Geriatrics', 'Hematology', 'Infectious Disease', 'Internal Medicine', 'Medical Genetics', 'Nephrology', 'Neurology', 'Neurosurgery', 'Obstetrics and Gynecology', 'Oncology', 'Ophthalmology', 'Orthopedic Surgery', 'Otolaryngology (ENT)', 'Pathology', 'Pediatrics', 'Physical Medicine and Rehabilitation', 'Plastic Surgery', 'Preventive Medicine', 'Primary Care', 'Psychiatry', 'Pulmonology', 'Radiology', 'Radiation Oncology', 'Rheumatology', 'Urology', 'Vascular Surgery'],
        validation: {
          required: true,
        },
        dependency: { field: 'is_pi_physician', value: 'Yes' },
      },
      {
        itemType: 'question',
        name: 'research_team_summary',
        label:
          'Provide a descriptive summary of the research team that will be operationalizing this study and why they\'re qualified to run this study under the PI\'s leadership.<br><small>Include information about any physician and non-physician sub- or co-investigators, research coordinators, and other research staff. Feel free to include links to research team bios or provide them using the upload functionality of this submission if you feel it will help make your case.</small>',
        fieldType: 'text',
        placeholder: 'Describe the composition and qualifications of the research team',
        rows: 9,
        validation: {
          required: true,
          maxLength: 5000,
          pattern: /^[\s\S]*$/,
        },
      },
      {
        itemType: 'question',
        name: 'training_certifications',
        label:
          'The PI and research study staff must have human subjects protections training for the entire duration of the study - select the training(s) used by your study\'s team:',
        fieldType: 'dropdown-multiple',
        options: [
          'ACRP Certified Clinical Investigator Training',
          'CenterWatch: Protecting Study Volunteers in Research',
          'Collaborative IRB Training Initiative (CITI)',
          'DIA Certified Investigator (CCI)',
          'SOCRA Clinical Research Professional (CRP)',
          'Tri-Council Policy Statement online training (TCPS)',
          'Human Research Protection Foundational Training (OHRP)',
          'PHRP Online Training',
          'Other',
        ],
        validation: { required: true },
        dependency: { field: 'is_exempt_determination', value: 'No' },
      },
      {
        itemType: 'question',
        name: 'training_other_description',
        label:
          'Describe the training (and confirm it included human subject protections training):',
        fieldType: 'text',
        validation: {
          required: true,
          maxLength: 500,
          pattern: /^.*$/,
        },
        dependency: { field: 'training_certifications', value: 'Other' },
      },
      {
        itemType: 'question',
        name: 'admin_pending_action',
        label: 'Is there any pending administrative action or investigation involving the PI or other study personnel?',
        fieldType: 'radio',
        options: ['Yes', 'No'],
        validation: { required: true },
        dependency: { field: 'is_exempt_determination', value: 'No' },
      },
      {
        itemType: 'question',
        name: 'admin_action_individuals_list',
        label: 'List the individual(s) involved and their role(s) in the research:',
        fieldType: 'text',
        validation: {
          required: true,
          maxLength: 500,
          pattern: /^.*$/,
        },
        dependency: { field: 'admin_pending_action', value: 'Yes' },
      },
      {
        itemType: 'question',
        name: 'admin_action_start_dates',
        label: 'Provide the approximate start date(s) of occurrence:',
        fieldType: 'text',
        validation: {
          required: true,
          maxLength: 100,
          pattern: /^.*$/,
        },
        dependency: { field: 'admin_pending_action', value: 'Yes' },
      },
      {
        itemType: 'question',
        name: 'admin_action_issuing_entities',
        label: 'Name the entity(ies) issuing the action:',
        fieldType: 'text',
        validation: {
          required: true,
          maxLength: 200,
          pattern: /^.*$/,
        },
        dependency: { field: 'admin_pending_action', value: 'Yes' },
      },
      {
        itemType: 'question',
        name: 'admin_action_corrective_plan',
        label:
          'Provide a detailed history and any management or corrective action plan currently in place:',
        fieldType: 'text',
        validation: {
          required: true,
          maxLength: 1000,
          pattern: /^.*$/,
        },
        dependency: { field: 'admin_pending_action', value: 'Yes' },
      },
      {
        itemType: 'question',
        name: 'admin_action_documents',
        label:
          'Will all related documents including resolution steps be shared with this IRB via this study\'s document upload functionality?',
        fieldType: 'radio',
        options: ['Yes', 'No'],
        validation: { required: true },
        dependency: { field: 'admin_pending_action', value: 'Yes' },
      },
      {
        itemType: 'question',
        name: 'research_locations_confirmation',
        label:
          'Please confirm that all geographical locations where study participants will be seen or research will take place—and their respective functions associated with the proposed study—will be included in the study protocol or other documents uploaded with this study submission. <br><small>Include the company/institution/organization name of the site, street address, and study-related resources associated with the locations.</small>',
        fieldType: 'radio',
        options: ['Yes', 'No'],
        validation: { required: true },
        dependency: { field: 'is_exempt_determination', value: 'No' },
      },
      {
        itemType: 'question',
        name: 'location_pi_oversight',
        label: 'Explain how the PI will provide adequate oversight of the research location(s):',
        fieldType: 'text',
        placeholder: 'describe how the PI will provide oversight',
        rows: 4,
        validation: {
          required: true,
          maxLength: 2000,
          pattern: /^[\s\S]*$/,
        },
        dependency: { field: 'is_exempt_determination', value: 'No' },
      },
      {
        itemType: 'question',
        name: 'financial_conflict_disclosure',
        label:
          'Does the Principal Investigator (PI), the PI\'s immediate family, or any other research personnel or their immediate families, have any of the following financial interests in any entity that is sponsoring the research, or an entity that is manufacturing the product or service being tested? <br><small><ol><li>(1) Any remuneration from the entity in the previous twelve months that exceeds $5,000, when aggregated for the individual and their immediate family;</li><li>(2) Any equity interest in the entity;</li><li>(3) Any intellectual property rights and interests;</li><li>(4) Any governance or executive relationship with the entity.</li></ol></small>',
        fieldType: 'radio',
        options: ['Yes', 'No'],
        validation: { required: true },
      },
      {
        itemType: 'question',
        name: 'conflict_management_plan',
        label:
          'Describe the financial interests in great detail, and describe why, including a conflict management plan as appropriate in your description, for why these financial interests will NOT adversely affect the ethical conduct of the study by biasing the consent process or otherwise biasing the integrity of the research.',
        fieldType: 'text',
        placeholder: 'description of potential conflicts and conflict management plan',
        rows: 6,
        validation: {
          required: true,
          maxLength: 5000,
          pattern: /^[\s\S]*$/,
        },
        dependency: { field: 'financial_conflict_disclosure', value: 'Yes' },
      },
      {
        itemType: 'question',
        name: 'add_addl_contact',
        label:
          '(optional) Would you like to provide up to 2 additional contact emails for this research, <i>besides the Principal Investigator and the person filling out this form</i>, to receive e-mail notifications about the submission during the IRB review process?',
        fieldType: 'radio',
        options: ['Yes', 'No'],
        validation: { required: false },
      },
      {
        itemType: 'question',
        name: 'addl_contact_1_email',
        label: 'Additional Contact 1 - Email:',
        fieldType: 'text',
        validation: {
          required: false,
          maxLength: 254,
          pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        },
        dependency: { field: 'add_addl_contact', value: 'Yes' },
      },
      {
        itemType: 'question',
        name: 'addl_contact_2_email',
        label: 'Additional Contact 2 - Email:',
        fieldType: 'text',
        validation: {
          required: false,
          maxLength: 254,
          pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        },
        dependency: { field: 'add_addl_contact', value: 'Yes' },
      },
    ],
  },
  {
    heading: 'Prior IRB Review & IRB Transfer',
    items: [
      {
        itemType: 'question',
        name: 'prior_irb_review',
        label:
          'Has another IRB reviewed this research or site and decided to table, defer, disapprove, suspend, terminate, or decline to approve it?',
        fieldType: 'radio',
        options: ['Yes', 'No'],
        validation: { required: true },
      },
      {
        itemType: 'question',
        name: 'prior_irb_explanation',
        label: 'If Yes, provide a detailed explanation of the previous reviews by other IRBs:',
        fieldType: 'text',
        rows: 6,
        validation: {
          required: true,
          maxLength: 5000,
          pattern: /^[\s\S]*$/,
        },
        dependency: { field: 'prior_irb_review', value: 'Yes' },
      },
      {
        itemType: 'question',
        name: 'irb_transfer',
        label: 'Are you transferring IRB oversight from another IRB to this IRB?',
        fieldType: 'radio',
        options: ['Yes', 'No'],
        validation: { required: true },
        dependency: { field: 'is_exempt_determination', value: 'No' },
      },
      {
        itemType: 'question',
        name: 'irb_transfer_prev_irb_name',
        label: 'Name of Previous IRB:',
        fieldType: 'text',
        placeholder: 'Enter IRB name',
        validation: {
          required: true,
          maxLength: 200,
          pattern: /^.*$/,
        },
        dependency: { field: 'irb_transfer', value: 'Yes' },
      },
      {
        itemType: 'question',
        name: 'irb_transfer_prev_irb_website',
        label: 'Website of previous IRB:',
        fieldType: 'text',
        placeholder: 'Enter full website URL',
        validation: {
          required: true,
          maxLength: 200,
          pattern: /^(https?:\/\/.*)$/,
        },
        dependency: { field: 'irb_transfer', value: 'Yes' },
      },
      {
        itemType: 'question',
        name: 'irb_transfer_prev_irb_initial_date',
        label: 'Date of initial approval by the previous IRB:',
        fieldType: 'text',
        placeholder: 'use MM/DD/YYYY format',
        validation: {
          required: true,
          maxLength: 10,
          pattern: /^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])\/[0-9]{4}$/,
        },
        dependency: { field: 'irb_transfer', value: 'Yes' },
      },
      {
        itemType: 'question',
        name: 'irb_transfer_prev_irb_expiry_date',
        label: 'Date approval expires, if applicable (enter N/A if there is no expiration date):',
        fieldType: 'text',
        placeholder: 'use MM/DD/YYYY format or N/A',
        validation: {
          required: true,
          maxLength: 10,
          pattern: /^.*$/,
        },
        dependency: { field: 'irb_transfer', value: 'Yes' },
      },
      {
        itemType: 'question',
        name: 'irb_transfer_transfer_reason',
        label: 'Reason for transfer to Timely IRB:',
        fieldType: 'text',
        rows: 3,
        placeholder: 'Enter reason for transfer',
        validation: {
          required: true,
          maxLength: 1000,
          pattern: /^[\s\S]*$/,
        },
        dependency: { field: 'irb_transfer', value: 'Yes' },
      },
      {
        itemType: 'question',
        name: 'irb_transfer_study_begun',
        label: 'Has the study begun?',
        fieldType: 'radio',
        options: ['Yes', 'No'],
        validation: { required: true },
        dependency: { field: 'irb_transfer', value: 'Yes' },
      },
      {
        itemType: 'question',
        name: 'irb_transfer_enroll_more',
        label: 'Do you intend to enroll any more participants under the exact protocol approved by the previous IRB?',
        fieldType: 'radio',
        options: ['Yes', 'No'],
        validation: { required: true },
        dependency: { field: 'irb_transfer', value: 'Yes' },
      },
      {
        itemType: 'question',
        name: 'irb_transfer_subjects_active',
        label: 'Are any participants still in active study procedures under the exact protocol approved by the previous IRB?',
        fieldType: 'radio',
        options: ['Yes', 'No'],
        validation: { required: true },
        dependency: { field: 'irb_transfer', value: 'Yes' },
      },
      {
        itemType: 'question',
        name: 'irb_transfer_new_risk_info',
        label: 'Has any new risk or benefit information become available that was not reported to the previous IRB?',
        fieldType: 'radio',
        options: ['Yes', 'No'],
        validation: { required: true },
        dependency: { field: 'irb_transfer', value: 'Yes' },
      },
      {
        itemType: 'question',
        name: 'irb_transfer_new_risk_info_details',
        label: 'Submit the new risk or benefit information for review:',
        fieldType: 'text',
        rows: 3,
        placeholder: 'describe new risk or benefit information',
        validation: {
          required: true,
          maxLength: 5000,
          pattern: /^[\s\S]*$/,
        },
        dependency: {
          all: [
            { field: 'irb_transfer_new_risk_info', value: 'Yes' },
            { field: 'irb_transfer', value: 'Yes' },
          ],
        },
      },
      {
        itemType: 'question',
        name: 'irb_transfer_protocol_changes',
        label: 'Do you intend on making any changes to the protocol or other study materials approved previously by the other IRB?',
        fieldType: 'radio',
        options: ['Yes', 'No'],
        validation: { required: true },
        dependency: { field: 'irb_transfer', value: 'Yes' },
      },
      {
        itemType: 'question',
        name: 'irb_transfer_protocol_changes_details',
        label: 'Please provide: (1) a summary of the protocol changes, and (2) the rationale for why these changes are being made:',
        fieldType: 'text',
        rows: 6,
        placeholder: 'describe protocol changes or deviations and the rationale for the changes',
        validation: {
          required: true,
          maxLength: 5000,
          pattern: /^[\s\S]*$/,
        },
        dependency: {
          all: [
            { field: 'irb_transfer_protocol_changes', value: 'Yes' },
            { field: 'irb_transfer', value: 'Yes' },
          ],
        },
      },
      {
        itemType: 'question',
        name: 'irb_transfer_protocol_changes_included',
        label: 'Are the protocol changes included in the study protocol PDF and other relevant documentation uploaded with this submission?<br><small>Please upload a clean version of the amended protocol/documents as the "original" documents for review in the upload section and the redlined version(s) previously approved by the other IRB as "additional" files.</small>',
        fieldType: 'radio',
        options: ['Yes', 'No'],
        validation: { required: true },
        dependency: {
          all: [
            { field: 'irb_transfer_protocol_changes', value: 'Yes' },
            { field: 'irb_transfer', value: 'Yes' },
          ],
        },
      },
      {
        itemType: 'question',
        name: 'irb_transfer_unanticipated_problems',
        label:
          'Have there been any unanticipated problems related to this research? <br><small>Unanticipated problems include any incident, experience, or outcome that is unexpected, possibly related to research participation, and suggests subjects or others may be at greater risk than previously known.</small>',
        fieldType: 'radio',
        options: ['Yes', 'No'],
        validation: { required: true },
        dependency: { field: 'irb_transfer', value: 'Yes' },
      },
      {
        itemType: 'question',
        name: 'irb_transfer_unanticipated_problems_details',
        label: 'Describe the problem(s) and action(s) taken, if any:',
        fieldType: 'text',
        rows: 4,
        placeholder: 'describe unanticipated problems and actions taken',
        validation: {
          required: true,
          maxLength: 5000,
          pattern: /^[\s\S]*$/,
        },
        dependency: {
          all: [
            { field: 'irb_transfer_unanticipated_problems', value: 'Yes' },
            { field: 'irb_transfer', value: 'Yes' },
          ],
        },
      },
      {
        itemType: 'question',
        name: 'irb_transfer_subject_complaints',
        label: 'Have there been any participant complaints related to this research?',
        fieldType: 'radio',
        options: ['Yes', 'No'],
        validation: { required: true },
        dependency: { field: 'irb_transfer', value: 'Yes' },
      },
      {
        itemType: 'question',
        name: 'irb_transfer_subject_complaints_details',
        label: 'Describe the participant complaint(s) and action(s) taken to address them:',
        fieldType: 'text',
        rows: 3,
        placeholder: 'describe participant complaints and actions taken',
        validation: {
          required: true,
          maxLength: 5000,
          pattern: /^[\s\S]*$/,
        },
        dependency: {
          all: [
            { field: 'irb_transfer_subject_complaints', value: 'Yes' },
            { field: 'irb_transfer', value: 'Yes' },
          ],
        },
      },
      {
        itemType: 'question',
        name: 'irb_transfer_prev_irb_suspension',
        label: 'Did the previous IRB ever suspend or terminate this research at one or more sites?',
        fieldType: 'radio',
        options: ['Yes', 'No'],
        validation: { required: true },
        dependency: { field: 'irb_transfer', value: 'Yes' },
      },
      {
        itemType: 'question',
        name: 'irb_transfer_prev_irb_suspension_details',
        label:
          'Provide information on the reason, the steps taken to resume the research, and copies of any IRB correspondence related to the suspension:',
        fieldType: 'text',
        rows: 4,
        placeholder: 'provide details of IRB suspension or termination',
        validation: {
          required: true,
          maxLength: 2000,
          pattern: /^[\s\S]*$/,
        },
        dependency: {
          all: [
            { field: 'irb_transfer_prev_irb_suspension', value: 'Yes' },
            { field: 'irb_transfer', value: 'Yes' },
          ],
        },
      },
    ],
  },
  {
    heading: 'Billing Contact Information',
    description: 'Provide the best contact for billing purposes. An invoice will be sent when this form is submitted. The invoice is issued to the billing entity named below, or to the study sponsor if no billing entity is provided. Payments can be made via ACH or credit card, and payments must be completed before the IRB\'s review decision is released.',
    items: [
      {
        itemType: 'question',
        name: 'billing_contact_company',
        label: 'Billing entity legal name <br><small>The organization the invoice should be issued to (e.g., a CRO or site). Leave blank to invoice the study sponsor.</small>',
        fieldType: 'text',
        placeholder: 'Leave blank to invoice the study sponsor',
        validation: {
          required: false,
          maxLength: 200,
          pattern: /^.*$/,
        },
      },
      {
        itemType: 'question',
        name: 'billing_contact_first_name',
        label: 'First Name:',
        fieldType: 'text',
        validation: {
          required: true,
          maxLength: 100,
          pattern: /^.*$/,
        },
      },
      {
        itemType: 'question',
        name: 'billing_contact_last_name',
        label: 'Last Name:',
        fieldType: 'text',
        validation: {
          required: true,
          maxLength: 100,
          pattern: /^.*$/,
        },
      },
      {
        itemType: 'question',
        name: 'invoice_email',
        label:
          'What\'s the billing contact\'s email address?',
        fieldType: 'text',
        placeholder: 'Accounts Payable contact email',
        validation: {
          required: true,
          maxLength: 254,
          pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        },
      },
      {
        itemType: 'question',
        name: 'billing_contact_phone',
        label: 'Phone:',
        fieldType: 'text',
        placeholder: '10 digit phone number',
        validation: {
          required: false,
          minLength: 10,
          maxLength: 10,
          pattern: /^[0-9()+-\s]+$/,
        },
      },
    ],
  },
  {
    heading: 'Submission Documents Upload',
    description: 'Upload all documents required for review. Do not upload any documents with password protection.',
    items: [
      {
        itemType: 'document',
        name: 'doc_protocol',
        label: 'Research Study Protocol <br><small>The protocol must include a clear description of study objectives, methodology, participant selection criteria, informed consent procedures, data collection and analysis plans, risks and benefits assessment, privacy and confidentiality measures, study timelines, and plans for data safety monitoring, ensuring compliance with ethical standards and regulatory requirements.</small>',
        documentType: '.pdf',
        validation: {
          validate: {
            docCheck: (val: any) =>
              validateDocument(val) || 'Please upload a Protocol Document.',
          },
        },
      },
      {
        itemType: 'document',
        name: 'doc_human_subject_training',
        allowMultiple: true,
        label: 'Human Subject Training Documentation for all study personnel<br><small>Upload training certificates for all study team members.</small>',
        documentType: '.pdf',
        validation: {
          validate: {
            docCheck: (val: any) =>
              validateDocument(val) || 'Please upload at least one Human Subject Training Document.',
          },
        },
        dependency: { field: 'is_exempt_determination', value: 'No' },
      },
      {
        itemType: 'question',
        name: 'doc_selection',
        label:
          'Select the additional document type(s) you\'d like to upload:<br><small><i>Note:</i> If any of the document types in the list below are applicable to or used in your study, you <strong>must</strong> upload them.</small>',
        fieldType: 'dropdown-multiple',
        options: ['Study Personnel Resumes/CVs', 'Investigator\'s Brochure', 'Participant Recruitment Materials', 'Data Collection Tools (CRFs / Questionnaires /Surveys)', 'Additional Document Types', 'None of these documents apply to this study'],
        validation: { required: true },
      },
      {
        itemType: 'document',
        name: 'doc_consent_form',
        allowMultiple: true,
        label: 'Informed Consent Form<br><small>The consent form must clearly and accurately communicate the study\'s purpose, procedures, risks and potential benefits, compensation information,confidentiality protections, participant rights (including voluntary participation and the right to withdraw), contact information for questions, and acknowledgment of consent through participant signature or documented consent process, in compliance with regulatory and ethical guidelines. The phone number and email to use for Timely IRB in the ICF are 385-287-1138 and participants@timelyirb.com. </small>',
        documentType: '.pdf',
        validation: {
          validate: {
            docCheck: (val: any) =>
              validateDocument(val) || 'Please upload an Informed Consent Form.',
          },
        },
        dependency: { field: 'informed_consent', value: 'Yes' },
      },
      {
        itemType: 'document',
        name: 'doc_fwa_documentation',
        allowMultiple: true,
        label: 'Federal Wide Assurance (FWA) Documentation<br><small>Documentation of your institution\'s FWA number and expiration date. Easiest way to provide this is to go to the <b><span style="color: blue;"><a href="https://ohrp.cit.nih.gov/search/FwaDtl.aspx" target="_blank" rel="noopener noreferrer">OHRP FWA search tool</a></span></b>, find your institution\'s assurance page, and get a screenshot.</small>',
        documentType: '.pdf',
        validation: {
          validate: {
            docCheck: (val: any) =>
              validateDocument(val) || 'Please upload Federalwide Assurance (FWA) Documentation.',
          },
        },
        dependency: { field: 'is_fed_funded', value: 'Yes' },
      },
      {
        itemType: 'document',
        name: 'doc_ind_fda_letter',
        allowMultiple: true,
        label:
          'FDA IND / Expanded-Access Correspondence<br><small>Upload the FDA “safe to proceed” letter, expanded-access authorization, or related IND correspondence for this single-patient use.</small>',
        documentType: '.pdf',
        validation: {
          validate: {
            docCheck: (val: any) =>
              validateDocument(val) ||
              'Please upload the FDA IND or expanded-access authorization letter/correspondence.',
          },
        },
        dependency: {
          field: 'ind_conduct',
          value: 'Yes',
        },
      },
      {
        itemType: 'document',
        name: 'doc_irb_auth_agreement',
        allowMultiple: true,
        label: 'IRB Authorization Agreement. <br><small>We recommend using <b><span style="color: blue;"><a href="https://www.hhs.gov/ohrp/sites/default/files/ohrp/assurances/forms/irbauthorizpdf.pdf" target="_blank" rel="noopener noreferrer">this simple form agreement from HHS</a></span></b>. Ensure it is signed by your institution\'s signatory. Timely IRB\'s registration number is IRB00014644.</small>',
        documentType: '.pdf',
        validation: {
          validate: {
            docCheck: (val: any) =>
              validateDocument(val) || 'Please upload IRB Authorization Agreement.',
          },
        },
        dependency: { field: 'is_fed_funded', value: 'Yes' },
      },
      {
        itemType: 'document',
        name: 'doc_administrative_action',
        allowMultiple: true,
        label: 'Administrative Action Documentation',
        documentType: '.pdf',
        validation: {
          validate: {
            docCheck: (val: any) =>
              validateDocument(val) || 'Please upload Administrative Action Documentation.',
          },
        },
        dependency: {
          all: [
            { field: 'admin_pending_action', value: 'Yes' },
            { field: 'admin_action_documents', value: 'Yes' }
          ]
        },
      },
      {
        itemType: 'document',
        name: 'doc_study_personnel_resumes',
        allowMultiple: true,
        label: 'Study Personnel Resumes/CVs<br><small>Upload resumes or CVs for key study team personnel.</small>',
        documentType: '.pdf',
        validation: {
          validate: {
            docCheck: (val: any) =>
              validateDocument(val) || 'Please upload at least one Study Personnel Resume/CV.',
          },
        },
        dependency: { field: 'doc_selection', value: 'Study Personnel Resumes/CVs' },
      },
      {
        itemType: 'document',
        name: 'doc_investigator_brochure',
        allowMultiple: true,
        label: 'Investigator\'s Brochure',
        documentType: '.pdf',
        validation: {
          validate: {
            docCheck: (val: any) =>
              validateDocument(val) || 'Please upload an Investigator\'s Brochure.',
          },
        },
        dependency: { field: 'doc_selection', value: 'Investigator\'s Brochure' },
      },
      {
        itemType: 'document',
        name: 'doc_recruitment_materials',
        allowMultiple: true,
        label: 'Participant Recruitment Materials<br><small>Examples: recruitment scripts, flyers, posters, brochures, etc.</small>',
        documentType: '.pdf',
        validation: {
          validate: {
            docCheck: (val: any) =>
              validateDocument(val) || 'Please upload at least one Participant Recruitment Material.',
          },
        },
        dependency: { field: 'doc_selection', value: 'Participant Recruitment Materials' },
      },
      {
        itemType: 'document',
        name: 'doc_data_collection_tools',
        allowMultiple: true,
        label: 'Data Collection <br><small>e.g., CRFs, Questionnaires, Surveys</small>',
        documentType: '.pdf',
        validation: {
          validate: {
            docCheck: (val: any) =>
              validateDocument(val) || 'Please upload at least one Data Collection Tool.',
          },
        },
        dependency: { field: 'doc_selection', value: 'Data Collection Tools (CRFs / Questionnaires /Surveys)' },
      },
      {
        itemType: 'document',
        name: 'doc_additional_files',
        allowMultiple: true,
        label: 'Additional Files<br><small>Upload any additional supporting documents.</small>',
        documentType: '.pdf',
        validation: {
          validate: {
            docCheck: (val: any) =>
              validateDocument(val) || 'Please upload at least one Additional File.',
          },
        },
        dependency: { field: 'doc_selection', value: 'Additional Document Types' },
      },
    ],
  },
];
